import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, 
  ArrowRight, 
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import SubscriptionRequest from '../../requests/vendor/SubscriptionRequest';
import { useDispatch, useSelector } from 'react-redux';
import { setUser } from '../../redux/UserSlice';
import { toast } from 'react-toastify';

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.user) || null;
  const { transactionId, paymentDetails } = location.state || {};
  const [showModal, setShowModal] = React.useState(false);

  useEffect(() => {
    if (transactionId && paymentDetails) {
      sessionStorage.setItem('transactionId', transactionId);
      sessionStorage.setItem('paymentDetails', JSON.stringify(paymentDetails));
    }
  }, [transactionId, paymentDetails]);

  const currentTransactionId = transactionId || sessionStorage.getItem('transactionId');
  const currentPaymentDetails = paymentDetails || JSON.parse(sessionStorage.getItem('paymentDetails') || 'null');

  useEffect(() => {
    sessionStorage.setItem('paymentSuccessPage', 'true');
    const preventBackButton = (e) => {
      e.preventDefault();
      window.history.pushState(null, '', window.location.href);
      setShowModal(true);
    };
    if (sessionStorage.getItem('paymentSuccessPage') === 'true') {
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', preventBackButton);
    }

    return () => {
      window.removeEventListener('popstate', preventBackButton);
      sessionStorage.removeItem('paymentSuccessPage');
    };
  }, []);

  useEffect(() => {
    if (!currentTransactionId || !currentPaymentDetails) {
      navigate('/', { replace: true });
    }
  }, [currentTransactionId, currentPaymentDetails, navigate]);

  const clearPaymentSession = () => {
    sessionStorage.removeItem('paymentSuccessPage');
    sessionStorage.removeItem('transactionId');
    sessionStorage.removeItem('paymentDetails');
  };

  const handleDashboardNavigation = async () => {
    try {
      const response = await SubscriptionRequest.fetchUserCurrentSubscription();
      
      if (response.data?.status_code === '000' && !response?.data?.in_error) {
        dispatch(setUser({
          ...user,
          subscription: response.data.data
        }));
        clearPaymentSession();
        navigate('/dashboard', { replace: true });
      } else {
        toast.error(response?.data?.reason || 'Failed to update subscription status');
        return;
      }
    } catch (error) {
      console.error('Error fetching subscription details:', error);
      toast.error('Unable to verify subscription. Redirecting to dashboard...');
      clearPaymentSession();
      navigate('/dashboard', { replace: true });
    }
  };

  if (!currentTransactionId || !currentPaymentDetails) {
    return null;
  }

  return (
    <>
    <Navbar/>
    <div className="min-h-screen bg-[#fafafa] relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-green-100/30 to-emerald-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-r from-emerald-100/30 to-green-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 px-4 py-16 mx-auto max-w-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] shadow-2xl p-8 border border-green-100"
        >
          <div className="space-y-6 text-center">
            {/* Success Icon */}
            <div className="flex justify-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ 
                  type: "spring",
                  stiffness: 260,
                  damping: 20 
                }}
                className="flex justify-center items-center w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
              >
                <CheckCircle2 className="w-12 h-12 text-white" />
              </motion.div>
            </div>
            <div className="space-y-2">
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600"
              >
                Payment Successful!
              </motion.h1>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-gray-600"
              >
                Your payment has been processed successfully. Thank you for your purchase!
              </motion.p>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-6 space-y-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl"
            >
              <div className="flex justify-between items-center pb-4 border-b border-green-100">
                <span className="text-gray-600">Transaction ID</span>
                <span className="font-medium text-gray-900">{currentTransactionId}</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-green-100">
                <span className="text-gray-600">Package</span>
                <span className="font-medium text-gray-900">{currentPaymentDetails.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Amount Paid</span>
                <span className="text-xl font-bold text-green-600">
                  GH₵{parseFloat(currentPaymentDetails.price).toFixed(2)}
                </span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <button
                onClick={handleDashboardNavigation}
                className="py-4 w-full font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl transition-all duration-300 hover:shadow-lg group"
              >
                <span className="flex justify-center items-center space-x-2">
                  <span>Continue to Dashboard</span>
                  <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
    {showModal && (
      <div className="flex fixed inset-0 z-50 justify-center items-center bg-black/50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="p-6 m-4 w-full max-w-sm bg-white rounded-2xl shadow-xl"
        >
          <div className="space-y-4 text-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Please Use Dashboard Button
            </h3>
            <p className="text-gray-600">
              To ensure proper navigation, please use the "Continue to Dashboard" button below.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-3 w-full font-medium text-white bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl transition-all duration-300 hover:shadow-lg"
              >
                Got it
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  navigate('/dashboard');
                }}
                className="px-4 py-3 w-full font-medium text-green-600 bg-white rounded-xl border border-green-500 transition-all duration-300 hover:shadow-lg"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    )}
    </>
  );
};

export default PaymentSuccess; 